import api from './api';

export interface AdminUserListItem {
  userId: string;
  email: string;
  ruolo: string;
  nome: string;
  cognome: string;
  ragioneSociale?: string | null;
  partitaIva?: string | null;
  codiceFiscale?: string | null;
  telefono?: string | null;
  cellulare?: string | null;
  pec?: string | null;
  codiceSdi?: string | null;
  indirizzo?: string | null;
  cap?: string | null;
  citta?: string | null;
  provincia?: string | null;
  sedeOperativa?: { indirizzo: string; cap: string; citta: string; provincia: string } | null;
  attivo: boolean;
  rejected: boolean;
  rejectedAt?: string | null;
  idCliente?: number | null;
  idAgente?: number | null;
  createdAt?: string | null;
  /** `"contatto"` per un account attivo senza Cliente NTS. */
  erpStatus?: string | null;
  /** Account attivo senza Cliente: nasce al primo ordine. */
  contatto?: boolean;
  hasOrders: boolean;
  marcheInteresse: string[];
  parcoMacchineStimato?: string | null;
}

export interface AdminUserListResponse {
  items: AdminUserListItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface AdminApprovalLogItem {
  userId: string;
  ragioneSociale?: string | null;
  email: string;
  approvedByUserId: string;
  approvedByEmail: string;
  approvedAt: string;
  action: 'approved' | 'revoked' | 'rejected';
}

export interface AdminApprovalLogResponse {
  items: AdminApprovalLogItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/** Cliente del Gestionale con la stessa Partita IVA di una Registrazione. */
export interface CorrispondenzaGestionale {
  an_conto: number;
  ragioneSociale: string | null;
  citta: string | null;
  /** Il Titolare già collegato al conto: se c'è, Collega non è ammesso. */
  titolare: { userId: string; email: string } | null;
}

/** Scelta dell'admin quando il Gestionale ha già la Partita IVA. */
export type SceltaApprovazione = { collegaConto: number } | { creaComunque: true };

export const adminUserService = {
  /**
   * Elenco paginato registrazioni web (ruolo=cliente).
   * `attivo=false` → in attesa di approvazione, `attivo=true` → approvati,
   * `undefined` → tutte le registrazioni. `rejected` filtra separatamente
   * lo stato "Respinto" (richiesta utente 24/02/2026).
   */
  async list(opts: { attivo?: boolean; rejected?: boolean; q?: string; page?: number; limit?: number } = {}): Promise<AdminUserListResponse> {
    const params = new URLSearchParams();
    if (opts.attivo !== undefined) params.set('attivo', String(opts.attivo));
    if (opts.rejected !== undefined) params.set('rejected', String(opts.rejected));
    if (opts.q) params.set('q', opts.q);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    const { data } = await api.get<AdminUserListResponse>(`/api/auth/users${qs ? `?${qs}` : ''}`);
    return data;
  },

  /** Una Registrazione, per la sua scheda (Admin → Registrazioni → Scheda Utente). */
  async get(userId: string): Promise<AdminUserListItem> {
    const { data } = await api.get<AdminUserListItem>(`/api/auth/users/${userId}`);
    return data;
  },

  /** Approva una registrazione (attivo=false -> true). Pulisce anche un
   * eventuale flag "respinto" precedente (correzione di un rifiuto per errore).
   * Senza Collega crea prima il cliente sul gestionale: se non riesce
   * risponde con un errore e la registrazione resta in attesa. */
  async approve(userId: string, scelta?: SceltaApprovazione): Promise<AdminUserListItem> {
    const { data } = await api.patch<AdminUserListItem>(`/api/auth/users/${userId}/activate`, { attivo: true, ...scelta });
    return data;
  },

  /** Clienti del Gestionale con la stessa Partita IVA della Registrazione:
   * se ce ne sono, l'Approvazione chiede Collega o Crea comunque. */
  async corrispondenze(userId: string): Promise<CorrispondenzaGestionale[]> {
    const { data } = await api.get<{ corrispondenze: CorrispondenzaGestionale[] }>(`/api/auth/users/${userId}/corrispondenze`);
    return data.corrispondenze;
  },

  /** Respinge una registrazione in attesa (nuovo stato "Respinto", distinto
   * da "In attesa" — richiesta utente 24/02/2026). Consentito solo se non
   * ancora approvata. */
  async reject(userId: string): Promise<AdminUserListItem> {
    const { data } = await api.post<AdminUserListItem>(`/api/auth/users/${userId}/reject`);
    return data;
  },

  /** Revoca un'approvazione (attivo=true -> false). Consentito dal backend SOLO
   * se il cliente non ha ancora effettuato ordini sul gestionale ERP — altrimenti
   * risponde 409 (in quel caso il blocco va gestito direttamente da NTS). */
  async revoke(userId: string): Promise<AdminUserListItem> {
    const { data } = await api.patch<AdminUserListItem>(`/api/auth/users/${userId}/activate`, { attivo: false });
    return data;
  },

  async approvalLog(opts: { page?: number; limit?: number } = {}): Promise<AdminApprovalLogResponse> {
    const params = new URLSearchParams();
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    const { data } = await api.get<AdminApprovalLogResponse>(`/api/auth/users/approval-log${qs ? `?${qs}` : ''}`);
    return data;
  },
};
